import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ThemeChoice } from "@/lib/theme";

type ViewMode = "household" | "personal";

export type DateRangePreset =
  | "this-week"
  | "last-week"
  | "this-month"
  | "last-month"
  | "this-year"
  | "last-year"
  | "all-time"
  | "custom";

export interface TxDateRange {
  preset: DateRangePreset;
  /** Solo se usa cuando preset === "custom". ISO yyyy-mm-dd. */
  customFrom?: string;
  customTo?: string;
}

interface AppState {
  year: number;
  month: number; // 1-12
  viewMode: ViewMode;
  activeCurrency: string;
  txDateRange: TxDateRange;
  numbersHidden: boolean;
  /** Preferencia de tema del usuario. "system" sigue al SO. */
  theme: ThemeChoice;
  setMonth: (year: number, month: number) => void;
  setViewMode: (m: ViewMode) => void;
  setActiveCurrency: (c: string) => void;
  setTxDateRange: (r: TxDateRange) => void;
  toggleNumbersHidden: () => void;
  setTheme: (t: ThemeChoice) => void;
}

const now = new Date();

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      viewMode: "household",
      activeCurrency: "MXN",
      txDateRange: { preset: "this-month" },
      numbersHidden: false,
      // Default "dark": preserva la identidad de marca para usuarios actuales.
      // El usuario puede cambiar a "light" o "system" en Configuración.
      theme: "dark",
      setMonth: (year, month) => set({ year, month }),
      setViewMode: (viewMode) => set({ viewMode }),
      setActiveCurrency: (activeCurrency) => set({ activeCurrency }),
      setTxDateRange: (txDateRange) => set({ txDateRange }),
      toggleNumbersHidden: () => set((s) => ({ numbersHidden: !s.numbersHidden })),
      setTheme: (theme) => set({ theme }),
    }),
    { name: "finance-app-store" },
  ),
);
